import React, { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Button, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import { catchErrorMessage } from 'utility/helpers';
import ActionModal from 'components/common/modal/ActionModal';
import useUserData, { UserType } from 'hooks/useUserData';
import UserAPI from 'api/UserAPI';
import Avatar from 'components/common/avatar/Avatar';
import { Box } from '@mui/system';
import Upload from 'components/common/upload/Upload';
import { updateUserDetailsCache } from '@buildwise/libs/people/queries/useUserProfileData';

const $UploadBtn = styled(Button, { label: 'UploadBtn' })(({ theme }) => ({
  height: 42,
  borderRadius: 33,
  fontSize: 14,
  marginTop: 32,
}));

type UpdateProfilePhotoModalProps = {
  isLoggedInUser?: boolean;
  open: boolean;
  profileImageURL?: string;
  user: UserType;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

const UpdateProfilePhotoModal: React.FC<UpdateProfilePhotoModalProps> = ({
  isLoggedInUser = false,
  open,
  profileImageURL,
  user,
  setOpen,
}) => {
  const acceptedFiles = ['image/jpg', 'img/jpeg', 'img/png', '.png', '.jpg', '.jpeg'];
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | undefined>(undefined);
  const [filePreview, setFilePreview] = useState<string>('');
  const { companyId } = useUserData();

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files && event.target.files[0];
    if (uploadedFile) {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(uploadedFile);
      fileReader.onload = () => {
        setFile(uploadedFile);
        setFilePreview(fileReader.result as string);
      };
    }
  };

  const handleSave = async () => {
    try {
      if (!user || !file) return null;
      setLoading(true);
      const payload = {
        file,
        fileReferenceObject: {
          objectId: user.id,
          objectName: 'User',
          objectType: 'USER_PROFILE_IMAGE',
        },
      };

      const { data } = await UserAPI.updateProfilePicture(companyId, payload);
      updateUserDetailsCache({ ...user, profileImageURL: data?.attachments?.[0]?.fileURL || '' });
      toast.success('Profile photo is updated');
      setOpen(false);
    } catch (e) {
      toast.error(catchErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const removeProfilePhoto = async () => {
    try {
      setLoading(true);
      const payload = {
        ...user,
        profileImageURL: '',
      };

      const { data } = await UserAPI.editUser(payload);
      updateUserDetailsCache({ ...user, ...data, profileImageURL: '' });
      toast.success('Profile photo is removed');
      setOpen(false);
    } catch (e) {
      toast.error(catchErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ActionModal width={620} onClose={() => setOpen(false)} title="Profile Photo" open={open}>
      <Box display="flex" alignItems="center" flexDirection="column">
        <Upload
          doNotUpload={!isLoggedInUser}
          onChange={handleFileChange}
          acceptedFiles={acceptedFiles}
        >
          <Avatar
            src={filePreview || profileImageURL}
            fontSize={150}
            name={user.userNameToDisplay}
            size={isLoggedInUser ? 250 : 400}
          />
        </Upload>
        {isLoggedInUser && !file && (
          <Upload onChange={handleFileChange} acceptedFiles={acceptedFiles}>
            <$UploadBtn variant="contained">
              {profileImageURL ? 'Change Photo' : 'Upload Photo'}
            </$UploadBtn>
          </Upload>
        )}
        {isLoggedInUser && file && (
          <>
            {' '}
            <$UploadBtn variant="contained" disabled={!file || loading} onClick={handleSave}>
              {loading ? <CircularProgress sx={{ color: '#000' }} size={20} /> : 'Save Changes'}
            </$UploadBtn>
          </>
        )}
        {isLoggedInUser && profileImageURL && (
          <$UploadBtn
            sx={{ marginTop: '16px', color: '#A40E0E' }}
            onClick={removeProfilePhoto}
            disabled={loading}
          >
            {loading ? <CircularProgress sx={{ color: '#000' }} size={20} /> : 'Remove'}
          </$UploadBtn>
        )}
      </Box>
    </ActionModal>
  );
};

export default UpdateProfilePhotoModal;
